import { DataLoaderJobType } from 'app/modules/investigations/types/responses';
import { ColdStorageLoadingStatus } from 'app/shared/utils/fastArchHelpers';
import {
  U21Alert,
  U21AlertProps,
  U21Loading,
} from 'app/shared/u21-ui/components';
import { useMemo } from 'react';
import styled, { css } from 'styled-components';

const AlertLoadingIcon: U21AlertProps['Icon'] = (props) => (
  <StyledSpinner delay={0} loading variant="spinner" {...props} />
);

// Create a map of cgdoType to the message to show
const cgdoTypeToLabel: { [key in DataLoaderJobType]: string } = {
  txn_event: 'transactions',
  instrument: 'instruments',
  action_event: 'action events',
  entity: 'entities',
  entity_txn_event_assoc: '',
  instrument_txn_event_assoc: '',
};

interface OwnProps {
  coldStorageStatus: ColdStorageLoadingStatus;
  cgdoType: DataLoaderJobType;
  showOnSlowLoading?: boolean;
}

interface BatchProps {
  coldStorageStatuses: {
    [key in DataLoaderJobType]: ColdStorageLoadingStatus;
  };
  cgdoTypes: DataLoaderJobType[];
  showOnSlowLoading?: boolean;
}

type MixedProps = OwnProps | BatchProps;

const isBatchProps = (props: MixedProps): props is BatchProps => {
  return 'coldStorageStatuses' in props;
};

// Callers opt in to showing the message before the full LOADING_TIMEOUT via
// showOnSlowLoading; otherwise SLOW_LOADING is treated like plain LOADING.
const shouldShowMessage = (
  status: ColdStorageLoadingStatus,
  showOnSlowLoading?: boolean,
): boolean =>
  status === ColdStorageLoadingStatus.LOADING_TIMEOUT ||
  Boolean(
    showOnSlowLoading && status === ColdStorageLoadingStatus.SLOW_LOADING,
  );

const ColdStorageTimeoutComponent = ({ cgdoLabel }: { cgdoLabel: string }) => {
  return (
    <U21Alert
      title="We are looking for more data"
      Icon={AlertLoadingIcon}
      color="primary"
    >
      Some information regarding {cgdoLabel} isn’t readily available. Bear with
      us, this can take up to 5 minutes.
    </U21Alert>
  );
};

const BatchColdStorageCGDOMessage = ({
  coldStorageStatuses,
  cgdoTypes,
  showOnSlowLoading,
}: BatchProps) => {
  const labelsWithLoadingTimeout = useMemo(() => {
    const labelsToShow = cgdoTypes
      .filter((cgdoType) =>
        shouldShowMessage(coldStorageStatuses[cgdoType], showOnSlowLoading),
      )
      .map((cgdoType) => cgdoTypeToLabel[cgdoType]);
    return new Intl.ListFormat('en-US').format(labelsToShow);
  }, [coldStorageStatuses, cgdoTypes, showOnSlowLoading]);

  if (labelsWithLoadingTimeout.length > 0) {
    return <ColdStorageTimeoutComponent cgdoLabel={labelsWithLoadingTimeout} />;
  }
  return null;
};

export const ColdStorageCGDOMessage = (props: MixedProps) => {
  if (isBatchProps(props)) {
    return <BatchColdStorageCGDOMessage {...props} />;
  }
  // We are in the single CGDO case, show the label for a single object
  const { coldStorageStatus, cgdoType, showOnSlowLoading } = props;
  if (shouldShowMessage(coldStorageStatus, showOnSlowLoading)) {
    const cgdoLabel = cgdoTypeToLabel[cgdoType];
    return <ColdStorageTimeoutComponent cgdoLabel={cgdoLabel} />;
  }
  // No loading timeout, no message to show
  return null;
};

const StyledSpinner = styled(U21Loading)`
  ${(props) => {
    return css`
      svg {
        color: ${props.theme.palette.primary.main};
      }
    `;
  }}
`;
